// 📄 pages/Ariba/sections/KnownErrorsCatalogToolbox.tsx
// Catálogo global de Errores Conocidos, accesible como sección de primer
// nivel del Ariba Toolbox — reemplaza a la antigua "Errores comunes"
// embebida en AribaHome.tsx (acotada solo al módulo 'ariba'). Mismo
// patrón que InterfaceCatalogToolbox.tsx / TransactionCatalogToolbox.tsx:
// standalone, sin proyectoId, filtrado 100% client-side.
import { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, AlertTriangle } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import {
  KnownError, getAllKnownErrors, matchesSearch, isKnownErrorActivo,
  SISTEMAS_AFECTADOS, ALL_KNOWN_ERROR_SOLUCIONES
} from '../../../services/knownErrorsService.ts';
import KnownErrorCard from '../components/KnownErrorCard.tsx';
import KnownErrorDetailModal from '../components/KnownErrorDetailModal.tsx';

interface KnownErrorsCatalogToolboxProps {
  setPage?: (page: string) => void;
}

export default function KnownErrorsCatalogToolbox({ setPage }: KnownErrorsCatalogToolboxProps) {
  const [errors, setErrors] = useState<KnownError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroSistema, setFiltroSistema] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [selectedError, setSelectedError] = useState<KnownError | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllKnownErrors();
        setErrors(data.filter(isKnownErrorActivo));
      } catch (err) {
        console.error('Error cargando el catálogo de errores conocidos:', err);
        setError('No se pudo cargar el catálogo de errores conocidos.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtrados = useMemo(() => {
    return errors.filter((e) => {
      if (filtroSistema && e.sistemaAfectado !== filtroSistema) return false;
      if (filtroSolucion && e.solucion !== filtroSolucion) return false;
      return matchesSearch(e, busqueda);
    });
  }, [errors, busqueda, filtroSistema, filtroSolucion]);

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="errores-conocidos-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Errores Conocidos</h1>
          <p className="text-sm text-gray-400 mt-1">Errores documentados de integración SAP Ariba ↔ ERP con causa, solución y referencia a notas oficiales.</p>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative sm:col-span-1">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por código, causa, solución o tag..."
              className={`${inputClass} pl-9`}
            />
          </div>
          <select value={filtroSistema} onChange={(e) => setFiltroSistema(e.target.value)} className={inputClass}>
            <option value="">Todas las plataformas/sistemas</option>
            {SISTEMAS_AFECTADOS.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <select value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)} className={inputClass}>
            <option value="">Todas las soluciones</option>
            {ALL_KNOWN_ERROR_SOLUCIONES.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">Cargando catálogo...</p>
          </div>
        ) : filtrados.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">
              {errors.length === 0 ? 'Todavía no hay errores registrados en el catálogo. Un Admin puede cargarlos desde /admin.' : 'Ningún error coincide con la búsqueda/filtros.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtrados.map((err) => (
              <KnownErrorCard key={err.id} error={err} onClick={() => setSelectedError(err)} />
            ))}
          </div>
        )}
      </div>

      {selectedError && (
        <KnownErrorDetailModal error={selectedError} onClose={() => setSelectedError(null)} />
      )}
    </main>
  );
}
